import { useState } from 'react';
import { Fingerprint, ShieldCheck } from 'lucide-react';
import { startRegistration } from '@simplewebauthn/browser';
import type { PublicKeyCredentialCreationOptionsJSON } from '@simplewebauthn/browser';
import { beginEnrollmentPasskey, finishEnrollmentPasskey } from '../api/auth';
import { useAuthStore } from '../store/auth';
import { getApiClient } from '../api/factory';
import { ApiError } from '../api/client';
import { useT } from '../i18n';
import { RecoveryCodesDialog } from '../components/RecoveryCodesDialog';

interface Props {
  enrollmentSession: string;
  onCompleted: () => Promise<void> | void;
}

// Страница forced-enrollment: tenant policy require_mfa = true, оператор
// ещё не зарегистрировал passkey. Без неё access/refresh ему не выдадут.
//
// Flow: receive enrollmentSession (из /auth/login) → клик Register →
// startRegistration → finishEnrollmentPasskey → показать recovery codes
// → onCompleted (router редиректит в /).
export function ForcedEnrollmentView({ enrollmentSession, onCompleted }: Props) {
  const t = useT();
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codes, setCodes] = useState<string[] | null>(null);

  async function register() {
    setError(null);
    setPending(true);
    try {
      const begin = await beginEnrollmentPasskey(getApiClient(), enrollmentSession);
      const opts = begin.options as {
        publicKey: PublicKeyCredentialCreationOptionsJSON;
      };
      const attestation = await startRegistration({ optionsJSON: opts.publicKey });
      const result = await finishEnrollmentPasskey(getApiClient(), {
        enrollmentSession,
        sessionToken: begin.sessionToken,
        name: t('security.passkey.defaultName'),
        attestation,
      });
      useAuthStore.getState().setSession(result.session);
      setCodes(result.recoveryCodes);
    } catch (e) {
      if (e instanceof Error && (e.name === 'NotAllowedError' || e.name === 'AbortError')) {
        setError(t('common.cancel'));
      } else if (e instanceof ApiError) {
        setError(t('auth.enrollment.error'));
      } else {
        setError(t('common.unknownError'));
      }
    } finally {
      setPending(false);
    }
  }

  async function finish() {
    setCodes(null);
    await onCompleted();
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
      <div className="bg-white p-6 rounded-lg shadow-card border border-slate-200 w-full max-w-md">
        <div className="mb-3 flex items-center gap-2 text-accent-600">
          <ShieldCheck className="h-5 w-5" />
          <h1 className="text-lg font-semibold text-slate-900">
            {t('auth.enrollment.title')}
          </h1>
        </div>
        <p className="text-sm text-slate-600">{t('auth.enrollment.subtitle')}</p>

        {codes ? (
          <p className="mt-4 text-sm text-emerald-700">{t('auth.enrollment.success')}</p>
        ) : (
          <button
            type="button"
            disabled={pending}
            onClick={register}
            className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-accent-600 px-4 py-2 text-sm font-medium text-white hover:bg-accent-700 disabled:opacity-50"
          >
            <Fingerprint className="h-4 w-4" />
            {pending ? t('auth.enrollment.registering') : t('auth.enrollment.register')}
          </button>
        )}

        {error && (
          <p role="alert" className="mt-3 text-sm text-red-600">
            {error}
          </p>
        )}

        <RecoveryCodesDialog
          open={codes !== null}
          codes={codes ?? []}
          onClose={finish}
        />
      </div>
    </div>
  );
}
